import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { STRINGS, LANGS, type Lang, type Strings } from './strings'
import type { GameEvent } from '../game/types'
import { applyTranslation, loadTranslations, type TrMap } from '../data/i18n'

const STORAGE_KEY = 'lineup:lang'

const isLang = (v: string | null): v is Lang =>
  !!v && LANGS.some((l) => l.id === v)

/** ?lang= → saved choice → browser language → English. */
function detectLang(): Lang {
  try {
    const q = new URLSearchParams(window.location.search).get('lang')
    if (isLang(q)) return q
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isLang(saved)) return saved
    const nav = (navigator.language || 'en').slice(0, 2).toLowerCase()
    if (isLang(nav)) return nav
  } catch {
    /* ignore */
  }
  return 'en'
}

interface LangCtx {
  lang: Lang
  setLang: (l: Lang) => void
  t: Strings
  /** Localizes an event's title/blurb to the active language (English fallback). */
  localize: (ev: GameEvent) => GameEvent
}

const Ctx = createContext<LangCtx | null>(null)

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang)
  const [trMap, setTrMap] = useState<TrMap | undefined>(undefined)

  useEffect(() => {
    try {
      document.documentElement.setAttribute('lang', lang)
    } catch {
      /* ignore */
    }
  }, [lang])

  // Lazily fetch the active language's dataset; fall back to English until ready.
  useEffect(() => {
    let alive = true
    setTrMap(undefined)
    if (lang === 'en') return
    loadTranslations(lang).then((m) => {
      if (alive) setTrMap(m)
    })
    return () => {
      alive = false
    }
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      /* ignore */
    }
    setLangState(l)
  }, [])

  const localize = useCallback(
    (ev: GameEvent) => applyTranslation(ev, lang === 'en' ? undefined : trMap),
    [lang, trMap],
  )

  return (
    <Ctx.Provider value={{ lang, setLang, t: STRINGS[lang], localize }}>
      {children}
    </Ctx.Provider>
  )
}

export function useLang(): LangCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useLang must be used within <LangProvider>')
  return c
}
